
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
// Import from our custom wrapper instead of directly from sonner
import { toast } from '@/utils/sonner-toast-wrapper';
import { useNavigate } from 'react-router-dom';
import { PhoneNumberInput } from '@/components/invite-guests/phone-input/PhoneNumberInput';

interface SignUpFormProps {
  onLoading: (loading: boolean) => void;
  initialPhoneNumber?: string;
}

export const SignUpForm = ({ onLoading, initialPhoneNumber = '' }: SignUpFormProps) => {
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [name, setName] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onLoading(true);

    try {
      // Check if user exists in our users table
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('*')
        .eq('phone_number', phoneNumber)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingUser) {
        // User exists, redirect to login
        toast.info('Account already exists. Please login.');
        navigate('/login', { state: { phoneNumber: phoneNumber } });
        return;
      }

      // Proceed with new user signup flow - pass name in options.data
      const { error: authError } = await supabase.auth.signInWithOtp({
        phone: phoneNumber,
        options: {
          data: {
            name
          }
        }
      });

      if (authError) throw authError;

      // Store user details for later use during verification
      localStorage.setItem('pendingSignup', JSON.stringify({
        phoneNumber: phoneNumber,
        name,
      }));

      toast.success('Verification code sent to your phone!');
      navigate('/verify', { 
        state: { 
          phoneNumber: phoneNumber,
          mode: 'signup'
        } 
      });

    } catch (error: any) {
      console.error('Signup error:', error);
      toast.error(error.message || 'Failed to send verification code. Please try again.');
    } finally {
      onLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full">
      <div>
        <Input
          type="text"
          placeholder="Full Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div>
        <PhoneNumberInput
          value={phoneNumber}
          onChange={setPhoneNumber}
          required
          centerAlign
        />
      </div>
      
      <Button 
        className="w-full bg-primary text-white" 
        size="lg"
        type="submit"
        disabled={!phoneNumber || !name}
      >
        Let's go
      </Button>
    </form>
  );
};
